import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { BookingContact, Slot } from '@musicville/shared';

export interface BookingDraft {
  subjectSlug: string | null;
  subjectName: string | null;
  undecided: boolean;
  teacherId: string | null;
  teacherName: string | null;
  anyTeacher: boolean;
  slot: Slot | null;
  contact: Partial<BookingContact>;
  requestId: string;
}

interface BookingStore extends BookingDraft {
  setSubject: (slug: string, name: string) => void;
  setUndecided: () => void;
  setTeacher: (id: string | null, name: string | null) => void;
  setSlot: (slot: Slot | null) => void;
  setContact: (contact: Partial<BookingContact>) => void;
  clearSlot: () => void;
  reset: () => void;
}

function freshDraft(): BookingDraft {
  return {
    subjectSlug: null,
    subjectName: null,
    undecided: false,
    teacherId: null,
    teacherName: null,
    anyTeacher: false,
    slot: null,
    contact: { whatsappOptIn: true },
    requestId: crypto.randomUUID(),
  };
}

export const useBookingStore = create<BookingStore>()(
  persist(
    (set) => ({
      ...freshDraft(),

      setSubject: (slug, name) =>
        set((state) =>
          state.subjectSlug === slug && !state.undecided
            ? { subjectSlug: slug, subjectName: name }
            : {
                subjectSlug: slug,
                subjectName: name,
                undecided: false,
                teacherId: null,
                teacherName: null,
                anyTeacher: false,
                slot: null,
              },
        ),

      setUndecided: () =>
        set((state) =>
          state.undecided
            ? { undecided: true }
            : {
                undecided: true,
                subjectSlug: null,
                subjectName: null,
                teacherId: null,
                teacherName: null,
                anyTeacher: false,
                slot: null,
              },
        ),

      setTeacher: (id, name) =>
        set((state) => ({
          teacherId: id,
          teacherName: name,
          anyTeacher: id === null,
          slot: state.teacherId === id ? state.slot : null,
        })),

      setSlot: (slot) =>
        set(
          slot
            ? {
                slot,
                teacherId: slot.teacherId,
                teacherName: slot.teacherName,
              }
            : { slot: null },
        ),

      setContact: (contact) => set((state) => ({ contact: { ...state.contact, ...contact } })),

      clearSlot: () => set({ slot: null }),

      reset: () => set(freshDraft()),
    }),
    {
      name: 'mv.booking-draft',
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);

export const BOOKING_STEPS = [
  { path: '/booking/subject', key: 'booking.step1' },
  { path: '/booking/teacher', key: 'booking.step2' },
  { path: '/booking/time', key: 'booking.step3' },
  { path: '/booking/details', key: 'booking.step4' },
  { path: '/booking/review', key: 'booking.step5' },
] as const;
